import dayjs from 'dayjs';
import { apiSlice } from 'store/api/apiSlice';
import { splitStakeTotal } from 'utils/stakeSplit';
import { getPrimaryBetIdForLookup } from 'utils/ticketScan';
import { peelYamiData } from 'utils/yamiEnvelope';

/** 8-digit ticket, 10-digit legacy, or 6-char hex book code (API gateway). */
export function isOfflineRetailCode(raw: string): boolean {
  const t = raw.trim().replace(/\s/g, '');
  if (/^\d{8}$/.test(t)) return true;
  if (/^\d{10}$/.test(t)) return true;
  const h = t.toUpperCase().replace(/[^0-9A-F]/g, '');
  return h.length === 6 && /^[0-9A-F]{6}$/.test(h);
}

export function normalizeOfflineRetailCode(raw: string): string {
  const t = raw.trim().replace(/\s/g, '');
  if (/^\d{8}$/.test(t)) return t;
  if (/^\d{10}$/.test(t)) return t;
  return raw.trim().toUpperCase().replace(/[^0-9A-F]/g, '').slice(0, 6);
}

type OfflineSlipPayload = {
  code: string;
  kind?: string;
  betLegIds?: string[];
  stake: number;
  currency?: string;
  createdAt?: string;
  potentialPayout?: number;
  totalOdds?: number;
  selections: Array<{
    gameId: string;
    market: string;
    option: string;
    betType?: string;
    selection?: string;
    oddsDecimal?: number;
    rawOdds?: string;
    homeTeam?: string;
    awayTeam?: string;
    matchup?: string;
    league?: string;
    sport?: string;
    startTime?: string;
    matchLink?: string;
  }>;
};

function mapOfflineSlipToLegacy(
  slip: OfflineSlipPayload,
  legBets: Array<{
    legStatus?: string;
    status?: string;
    betKind?: string;
    legs?: Array<{ legStatus?: string; status?: string }>;
    cashierPayoutAt?: string | Date | null;
    _id?: string;
    id?: string;
  }> = [],
) {
  const selections = Array.isArray(slip.selections)
    ? slip.selections.filter((s) => s && typeof s === 'object')
    : [];
  if (!selections.length) {
    throw new Error('Offline slip has no valid selections');
  }
  const slipKind = slip.kind === 'wallet' ? 'wallet' : 'booking';
  const betLegIds = Array.isArray(slip.betLegIds) ? slip.betLegIds.map((x) => String(x || '').trim()) : [];
  const sharedAccaId =
    betLegIds.length === 1 && /^[a-fA-F0-9]{24}$/.test(betLegIds[0]!) ? betLegIds[0]! : null;
  const primaryBet = legBets[0] || null;
  const placed = slip.createdAt ? new Date(slip.createdAt) : new Date();
  const stakesPerLeg = splitStakeTotal(slip.stake, selections.length);

  const resolveLegBet = (index: number) => {
    if (legBets[index]) return legBets[index];
    if (legBets.length === 1) return legBets[0];
    return null;
  };

  const resolveLegStatus = (index: number, legBet: ReturnType<typeof resolveLegBet>) => {
    if (!legBet) return undefined;
    if (legBet.betKind === 'accumulator' && Array.isArray(legBet.legs) && legBet.legs[index]) {
      const accaLeg = legBet.legs[index];
      return accaLeg?.legStatus || accaLeg?.status || legBet.status;
    }
    return legBet.legStatus || legBet.status;
  };

  const betItems = selections.map((s, i) => {
    const game = s.matchup || `${s.homeTeam ?? 'Home'} vs ${s.awayTeam ?? 'Away'}`;
    const odd = Number(s.oddsDecimal ?? 1);
    const st = stakesPerLeg[i] ?? 0;
    const legId = betLegIds[i] || sharedAccaId;
    const rowId = legId && /^[a-fA-F0-9]{24}$/.test(legId) ? legId : `${slip.code}-${i}`;
    const legBet = resolveLegBet(i);
    const legSt = resolveLegStatus(i, legBet);
    const gs = legStatusToGameStatus(legSt, s.startTime);
    const ticketWon = String(primaryBet?.status || '').toLowerCase() === 'won';
    const ticketPaid = Boolean(primaryBet?.cashierPayoutAt);
    const rowStatus =
      gs === 'WON' || (ticketWon && gs !== 'LOST')
        ? ticketPaid
          ? 'REDEEMED'
          : 'WON'
        : gs === 'LOST'
          ? 'LOST'
          : 'PENDING';
    const alreadyPaid = ticketPaid;
    return {
      id: rowId,
      game,
      gameNumber: String(s.gameId),
      market: s.market,
      betType: s.betType && !String(s.betType).includes(' ') ? s.betType : undefined,
      selection: s.selection || s.option,
      picks: s.option,
      win: (st * odd).toFixed(2),
      stake: String(st),
      status: rowStatus,
      redeemable: ticketWon && !alreadyPaid,
      gameStatus: gs === 'PENDING' && ticketWon ? 'WON' : gs,
      homeTeam: s.homeTeam,
      awayTeam: s.awayTeam,
      league: s.league,
      sport: s.sport,
      startTime: s.startTime,
      matchLink: s.matchLink,
      rawOdds: s.rawOdds || (Number.isFinite(odd) ? String(odd) : undefined),
    };
  });

  let status = slipKind === 'booking' ? 'OPEN' : 'PENDING';
  let redeemable = false;
  if (slipKind === 'wallet' && primaryBet) {
    const betStatus = String(primaryBet.status || '').toLowerCase();
    const alreadyPaid = Boolean(primaryBet.cashierPayoutAt);
    if (betStatus === 'won') {
      status = alreadyPaid ? 'REDEEMED' : 'WON';
      redeemable = !alreadyPaid;
    } else if (betStatus === 'lost') {
      status = 'LOST';
    } else if (betStatus === 'needs_review') {
      status = 'NEEDS_REVIEW';
    } else if (betStatus === 'cancelled' || betStatus === 'void') {
      status = 'CANCELLED';
    }
  } else if (slipKind === 'wallet') {
    const derived = betItems.every((row) => row.status === 'WON')
      ? { status: 'WON', redeemable: true }
      : betItems.some((row) => row.status === 'PENDING')
        ? { status: 'PENDING', redeemable: false }
        : null;
    if (derived) {
      status = derived.status;
      redeemable = derived.redeemable;
    }
  }

  return {
    id: slip.code,
    slipKind,
    date: dayjs(placed).format('YYYY/MM/DD'),
    time: dayjs(placed).format('HH:mm:ss'),
    status,
    redeemable,
    potentialWin: Number(
      (primaryBet as { result?: { winAmount?: number }; potentialWin?: number } | null)?.result
        ?.winAmount ??
        (primaryBet as { potentialWin?: number } | null)?.potentialWin ??
        slip.potentialPayout ??
        0,
    ),
    bet_items: betItems,
  };
}

export type YamiPlaceBetBody = {
  fixtureId: string | number;
  betType: string;
  selection: string;
  stake: number;
  odds: number;
  /** Retail sportsbook: required when fixtureId is only Postgres game_id — bet-service resolves via scraper. */
  homeTeam?: string;
  awayTeam?: string;
  startTime?: string;
  leagueName?: string;
  sport?: string;
  matchLink?: string;
};

export type YamiPlaceBetResponse = {
  success: boolean;
  message?: string;
  bet?: {
    id?: string;
    _id?: string;
    fixtureId?: string | number;
    betType?: string;
    selection?: string;
    stake?: number;
    odds?: number;
    potentialWin?: number;
    status?: string;
    placedAt?: string;
    matchInfo?: {
      homeTeam?: { name?: string };
      awayTeam?: { name?: string };
      league?: { name?: string };
      sport?: string;
      kickoffTime?: string;
    };
  };
  newBalance?: number;
};

export type YamiAccumulatorLegBody = Omit<YamiPlaceBetBody, 'stake'>;

export type YamiPlaceAccumulatorBody = {
  stake: number;
  legs: YamiAccumulatorLegBody[];
};

function normalizePlaceBetResponse(raw: unknown): YamiPlaceBetResponse {
  try {
    const body = peelYamiData(raw) as Record<string, unknown>;
    const r = { ...(typeof raw === 'object' && raw ? (raw as object) : {}), ...body } as YamiPlaceBetResponse;
    if (!r || typeof r !== 'object') return (raw ?? {}) as YamiPlaceBetResponse;
    const bet = r.bet ?? (body.bet as YamiPlaceBetResponse['bet']);
    if (!bet || typeof bet !== 'object' || Array.isArray(bet)) {
      const inferredOk = body.success === true || body.ok === true;
      return { ...r, success: inferredOk, bet: bet as YamiPlaceBetResponse['bet'] };
    }
    const id = (bet as { id?: unknown; _id?: unknown }).id ?? (bet as { _id?: unknown })._id;
    const success =
      r.success === true ||
      body.success === true ||
      body.ok === true ||
      (id != null && String(id).trim() !== '');
    if (id != null && String(id).trim() !== '') {
      return {
        ...r,
        success: Boolean(success),
        bet: { ...(bet as object), id: String(id) } as YamiPlaceBetResponse['bet'],
      };
    }
    return { ...r, success: Boolean(success) };
  } catch {
    return (raw ?? { success: false }) as YamiPlaceBetResponse;
  }
}

function legStatusToGameStatus(legStatus: string | undefined, startTime?: string): string {
  const s = String(legStatus || '').toLowerCase();
  if (s === 'won' || s === 'win') return 'WON';
  if (s === 'lost' || s === 'lose') return 'LOST';
  if (s === 'void' || s === 'cancelled' || s === 'canceled') return 'VOID';
  if (startTime) {
    const ms = Date.parse(startTime);
    if (Number.isFinite(ms) && ms > Date.now()) return 'OPEN';
  }
  return 'PENDING';
}

/** Maps Yami GET /api/bets/:id into the legacy Cancel / Redeem table shape */
function mapYamiBetToLegacy(res: {
  success?: boolean;
  bet?: {
    id?: string;
    _id?: string;
    ticketCode?: string | null;
    fixtureId?: string | number;
    betType?: string;
    selection?: string;
    stake?: number;
    odds?: number;
    potentialWin?: number;
    status?: string;
    placedAt?: string;
    cashierPayoutAt?: string | Date | null;
    betKind?: string;
    legs?: Array<{
      fixtureId?: string | number;
      betType?: string;
      selection?: string;
      odds?: number;
      legStatus?: string;
      matchInfo?: {
        homeTeam?: { name?: string };
        awayTeam?: { name?: string };
        league?: { name?: string };
        sport?: string;
        kickoffTime?: string;
      };
    }>;
    matchInfo?: {
      homeTeam?: { name?: string };
      awayTeam?: { name?: string };
      league?: { name?: string };
      sport?: string;
      kickoffTime?: string;
    };
  };
}) {
  const ok = res?.success === true || (res as { ok?: boolean }).ok === true;
  if (!ok || !res.bet) {
    throw new Error('Invalid bet response');
  }
  const b = res.bet;
  const placed = b.placedAt ? new Date(b.placedAt) : new Date();
  const id = String(b.id ?? b._id ?? '');
  const isActive = b.status === 'active';
  const isWon = b.status === 'won';
  const alreadyPaidOut = Boolean(b.cashierPayoutAt);
  const isNeedsReview = b.status === 'needs_review';
  const isPending = isActive;
  const legacyStatus = isPending
    ? 'PENDING'
    : isNeedsReview
      ? 'NEEDS_REVIEW'
      : b.status === 'won'
        ? alreadyPaidOut
          ? 'REDEEMED'
          : 'WON'
        : b.status === 'lost'
          ? 'LOST'
          : b.status === 'cancelled' || b.status === 'void'
            ? 'CANCELLED'
            : 'SETTLED';

  const isAcca = b.betKind === 'accumulator' && Array.isArray(b.legs) && b.legs.length > 0;
  const totalStake = Number(b.stake ?? 0);
  const numLegs = isAcca ? (b.legs?.length ?? 1) : 1;
  // For accumulators: split stake evenly so totals() sums correctly
  const perLegStake = isAcca ? (totalStake / numLegs).toFixed(2) : String(totalStake);
  const betItems = isAcca
    ? b.legs!.map((leg, i) => {
        const kick = leg.matchInfo?.kickoffTime;
        const legSt = String(leg.legStatus || (leg as { status?: string }).status || '').toLowerCase();
        const gs = legStatusToGameStatus(legSt, kick);
        const rowStatus =
          gs === 'WON'
            ? 'WON'
            : gs === 'LOST'
              ? 'LOST'
              : legacyStatus === 'PENDING'
                ? 'PENDING'
                : legacyStatus;
        const legOdds = Number(leg.odds ?? 0);
        // Compute per-leg win from its own stake × its own odds so mapLegacyRowToBetItem
        // can recover the correct individual leg odds as win/stake.
        const legWin = legOdds >= 1.01
          ? (totalStake / numLegs * legOdds).toFixed(2)
          : Number(b.potentialWin ?? 0).toFixed(2);
        return {
          id: `${id}-leg-${i}`,
          game: `${leg.matchInfo?.homeTeam?.name ?? 'Home'} vs ${leg.matchInfo?.awayTeam?.name ?? 'Away'}`,
          gameNumber: String(leg.fixtureId ?? ''),
          // Prefer raw feed market title; fall back to public label from API.
          market: (leg as { market?: string }).market || leg.betType || '',
          betType: (leg as { marketType?: string }).marketType || leg.betType || '',
          selection: leg.selection ?? '',
          picks: leg.selection ?? '',
          win: legWin,
          stake: perLegStake,
          rawOdds: legOdds >= 1.01 ? String(legOdds) : undefined,
          status: rowStatus,
          redeemable: isWon && !alreadyPaidOut,
          gameStatus: gs,
          homeTeam: leg.matchInfo?.homeTeam?.name,
          awayTeam: leg.matchInfo?.awayTeam?.name,
          league: leg.matchInfo?.league?.name,
          sport: leg.matchInfo?.sport,
          startTime: kick,
        };
      })
    : [
        {
          id,
          game: `${b.matchInfo?.homeTeam?.name ?? 'Home'} vs ${b.matchInfo?.awayTeam?.name ?? 'Away'}`,
          gameNumber: String(b.fixtureId ?? ''),
          market: (b as { market?: string }).market || b.betType || '',
          betType: (b as { marketType?: string }).marketType || b.betType || '',
          selection: b.selection ?? '',
          picks: b.selection ?? '',
          win: Number(b.potentialWin ?? 0).toFixed(2),
          stake: String(b.stake ?? 0),
          status: legacyStatus,
          redeemable: isWon && !alreadyPaidOut,
          gameStatus: legStatusToGameStatus(
            b.status === 'won' ? 'won' : b.status === 'lost' ? 'lost' : 'pending',
            b.matchInfo?.kickoffTime,
          ),
          homeTeam: b.matchInfo?.homeTeam?.name,
          awayTeam: b.matchInfo?.awayTeam?.name,
          league: b.matchInfo?.league?.name,
          sport: b.matchInfo?.sport,
          startTime: b.matchInfo?.kickoffTime,
        },
      ];

  return {
    id,
    /** Printed coupon on the slip — never print Mongo ObjectId as coupon. */
    ticketCode: String(b.ticketCode || '').trim() || id,
    slipKind: 'wallet' as const,
    date: dayjs(placed).format('YYYY/MM/DD'),
    time: dayjs(placed).format('HH:mm:ss'),
    status: legacyStatus,
    redeemable: isWon && !alreadyPaidOut,
    potentialWin: Number(
      (b as { result?: { winAmount?: number } }).result?.winAmount ?? b.potentialWin ?? 0,
    ),
    bet_items: betItems,
  };
}

export const betApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    placeYamiBet: builder.mutation<YamiPlaceBetResponse, YamiPlaceBetBody>({
      query: (body) => ({
        url: '/api/bets/place',
        method: 'POST',
        body,
      }),
      transformResponse: (raw: unknown): YamiPlaceBetResponse => normalizePlaceBetResponse(raw),
      invalidatesTags: ['CashierSummary', 'CashierProfile'],
    }),
    placeYamiAccumulator: builder.mutation<YamiPlaceBetResponse, YamiPlaceAccumulatorBody>({
      query: (body) => ({
        url: '/api/bets/place-accumulator',
        method: 'POST',
        body,
      }),
      transformResponse: (raw: unknown): YamiPlaceBetResponse => normalizePlaceBetResponse(raw),
      invalidatesTags: ['CashierSummary', 'CashierProfile'],
    }),
    getBet: builder.query({
      query: (rawBetId: string) => {
        const trimmed = rawBetId.trim().replace(/\s/g, '');
        const hex6 = trimmed.toUpperCase().replace(/[^0-9A-F]/g, '');
        // 6-char hex booking code (non-pure-numeric like 'AB12CD') → offline booking
        if (hex6.length === 6 && /^[0-9A-F]{6}$/.test(hex6) && !/^\d{6}$/.test(trimmed)) {
          return `/api/retail/offline-bookings/${hex6}?forCheck=1`;
        }
        // 8-digit or 10-digit retail coupons: bet-service resolves slip + linked Mongo bets (status, payout).
        if (/^\d{8}$/.test(trimmed) || /^\d{10}$/.test(trimmed)) {
          return `/api/bets/${trimmed}`;
        }
        // 6-7 digit numeric codes → MongoDB bet ticketCode
        if (/^\d{6,7}$/.test(trimmed)) {
          return `/api/bets/${trimmed}`;
        }
        if (isOfflineRetailCode(rawBetId)) {
          const code = normalizeOfflineRetailCode(rawBetId);
          return `/api/retail/offline-bookings/${code}?forCheck=1`;
        }
        return `/api/bets/${getPrimaryBetIdForLookup(rawBetId)}`;
      },
      transformResponse: (res: unknown) => {
        const body = peelYamiData(res);
        if (body.offline === true) {
          const slip = body.slip as OfflineSlipPayload | undefined;
          if (!slip || typeof slip !== 'object') {
            throw new Error('Offline booking response missing slip');
          }
          return mapOfflineSlipToLegacy(
            slip,
            Array.isArray(body.bets) ? (body.bets as Parameters<typeof mapOfflineSlipToLegacy>[1]) : [],
          );
        }
        const bet = body.bet as Parameters<typeof mapYamiBetToLegacy>[0]['bet'];
        if (!bet || typeof bet !== 'object') {
          throw new Error('Ticket not found or invalid response');
        }
        return mapYamiBetToLegacy({
          success: true,
          ok: true,
          bet,
        } as Parameters<typeof mapYamiBetToLegacy>[0]);
      },
    }),
    cancelBet: builder.mutation({
      query: (id: string) => ({
        url: `/api/bets/${getPrimaryBetIdForLookup(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CashierProfile', 'CashierSummary'],
      transformResponse: (res: { success?: boolean; message?: string }) => ({
        ...res,
        updated_at: new Date().toISOString(),
      }),
    }),
    markCashierPayout: builder.mutation<
      { success?: boolean; message?: string; alreadyRecorded?: boolean; cashierTillBalance?: number },
      { betId: string }
    >({
      query: ({ betId }) => ({
        url: `/api/bets/redeem/${getPrimaryBetIdForLookup(betId)}`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['CashierSummary', 'CashierProfile'],
      transformResponse: (res: {
        success?: boolean;
        message?: string;
        data?: { cashierTillBalance?: number; alreadyRecorded?: boolean };
        cashierTillBalance?: number;
        alreadyRecorded?: boolean;
      }) => ({
        success: res.success,
        message: res.message,
        alreadyRecorded: res.alreadyRecorded ?? res.data?.alreadyRecorded,
        cashierTillBalance: res.cashierTillBalance ?? res.data?.cashierTillBalance,
      }),
    }),
  }),
});

export const {
  usePlaceYamiBetMutation,
  usePlaceYamiAccumulatorMutation,
  useLazyGetBetQuery,
  useCancelBetMutation,
  useMarkCashierPayoutMutation,
} = betApiSlice;
